<template>
  <v-col cols="12">
    <v-card class="elevation-4 mb-4">

      <!-- SELECT TIMEFRAME -->
      <v-row class="fill-height mb-3" align="center" justify="space-around">
        <v-btn-toggle v-model="selectedChartTimeframe" dark>
          <template v-for="timeframe in chartTimeframes">
            <v-btn :value="timeframe" :key="timeframe">
              {{ timeframe | toDays }}
            </v-btn>
          </template>
        </v-btn-toggle>
      </v-row>

      <!-- TEXT STATS -->
      <v-row no-gutters>
        <template v-for="mailEvent in mailEvents">
          <stat-card :key="'stc-' + mailEvent" :label="mailEvent" :value="periodStats[mailEvent].count"
            :percentage="periodStats[mailEvent].percent" useCampaignDecorators small noIcon>
          </stat-card>
        </template>
      </v-row>

      <!-- CHART -->
      <div :style="ui.chartMaxHeight">
        <canvas :id="chartName"></canvas>
      </div>

      <!-- TITLE OF Chart -->
      <v-card-title primary-title>
        <div>
          <h3 class="text-h5 mb0">Recent Email Activity</h3>
        </div>
      </v-card-title>
    </v-card>
  </v-col>
</template>

<script>
import Chart from 'chart.js'
import { StatsApi } from '@/services'
import { StatsApiOrig } from '@/services/api/statsApi'
import { mapState } from 'vuex'
import { MAIL_EVENT_NAMES } from '@/constants'
import StatCard from '@/components/ui/StatCard'
import { CHART_COLORS } from '@/components/charts'
import * as R from 'ramda'

export default {
  name: 'daily-email-activity',
  components: {
    [StatCard.name]: StatCard
  },
  props: {
    chartPrefix: {
      type: String,
      require: true
    },
    dealer: {
      type: Object,
      required: false
    },
    campaign: {
      type: Object,
      required: false
    },
    byCampaign: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      ui: {
        chartMaxHeight: 'max-height: 500px; min-height: 260px;'
      },
      chart: null,
      chartTimeframes: [
        1, 7, 30, 60, 90
      ],
      selectedChartTimeframe: 7,
      mailEvents: MAIL_EVENT_NAMES,
      dailyStats: [] // Stats Data from Query...
    }
  },
  computed: {
    ...mapState({
      accountKey: state => R.path(['attributes', 'custom:account_key'], state.cognito.user)
    }),

    // dynamic so we can put multiple stats of this type on page
    chartName() {
      return this.chartPrefix + '-EmailDailyStats'
    },

    /**
     * Period Stats:
     *  {
     *     bounce: { count: 0, percent: 0 },
     *     click: { count: 0, percent: 0 },
     *  }
     */

    periodStats() {
      const pdStats = this.dailyStats.reduce((acc, eventStat) => {
        this.mailEvents.forEach(event => {
          const statCount = eventStat[event + '_count'] || 0
          acc[event] = acc[event] ? acc[event] : { count: 0 }
          acc[event].count += statCount
        })
        return acc
      }, {})

      // check to see if empty - 1st time through no data yet
      if (Object.keys(pdStats).length === 0) {
        // return a copy of everything initialized to 0
        return this.mailEvents.map(event => {
          return { [event]: { count: 0, percent: 0 } }
        }).reduce((acc, event) => {
          return { ...acc, ...event }
        }, {})
      } else {
        // run all calculations against the delivered count - delivered count is run only against sent
        const percentKeys = this.mailEvents.filter(event => !['sent', 'delivered'].includes(event))

        pdStats.delivered.percent = this.calculatePercentage(pdStats.delivered.count, pdStats.sent.count)
        percentKeys.forEach(event => {
          pdStats[event].percent = this.calculatePercentage(pdStats[event].count, pdStats.delivered.count)
        })
      }

      return pdStats
    },

    xLabels() {
      return R.uniq(R.pluck('event_date', this.dailyStats)).sort()
    },

    datasets() {
      // basically same as periodStats but with daily counts rather than total of all days
      const byDayStats = R.groupBy((stat) => stat.event_date)(this.dailyStats)
      const dsData = this.mailEvents.map(event => {
        const dailyCountsByEvt = this.xLabels.map((xDate) => {
          // on a per day basis, sum the daily stats for the same event type (because we multiple campaigns...)
          return byDayStats[xDate].reduce((acc, st) => {
            acc += st[event + '_count'] || 0
            return acc
          }, 0)
        })
        return { // outer map fn return
          label: event,
          data: dailyCountsByEvt,
          fill: true // turns on/off background coloring of the lines
        }
      })
      return dsData
    },

    lineChartData() {
      return {
        type: 'line',
        data: {
          labels: this.xLabels,
          datasets: this.datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          lineTension: 1,
          scales: {
            yAxes: [{
              ticks: {
                beginAtZero: true,
                padding: 25
              }
            }]
          },
          plugins: {
            colorschemes: {
              scheme: CHART_COLORS
            },
            datalabels: {
              display: false // this was the trick to turning off point labels - have to override plugin
            }
          }
        }
      }
    }
  },
  watch: {
    selectedChartTimeframe: {
      immediate: false,
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.$logger.trace('Loading of daily stats...')
          this.loadDailyStats()
        } else {
          this.$logger.trace('Skipping refresh of daily stats...')
        }
      }
    },
    lineChartData: {
      immediate: false,
      handler(newVal, oldVal) {
        this.$logger.trace('Refreshing Daily Stats Chart')
        this.chart.type = newVal.type
        this.chart.data = newVal.data
        this.chart.options = newVal.options
        this.chart.update()
      }
    },
    dealer: {
      immediate: false,
      handler(newDealer, oldDealer) {
        this.refreshChart()
      }
    },
    campaign: {
      immediate: false,
      handler(newCampaign, oldCampaign) {
        this.refreshChart()
      }
    }
  },
  methods: {
    findOrCreateChart(chartId, chartData) {
      if (this.chart) {
        return this.chart
      } else {
        const ctx = document.getElementById(chartId)
        const chart = new Chart(ctx, {
          type: chartData.type,
          data: chartData.data,
          options: chartData.options
        })
        this.chart = chart
        return chart
      }
    },
    calculatePercentage(numerator, denominator) {
      if (denominator === 0) return 0.0; // Prevent division by zero
      return ((numerator / 1.0) / (denominator / 1.0))
    },

    /**
     * Daily Stats:
     * {
     * }
     */
    loadDailyStats() {
      // temp keep this around - porting params to new API
      // in case we need to roll back, all need to do is swap newParams for statParams
      const newStatParams = { account_key: this.accountKey, ndays: this.selectedChartTimeframe }

      if (R.prop('dms_key', this.dealer)) {
        newStatParams.sender_key = this.dealer.dms_key
      }
      // if forcing byCampaign, do not load unless we have all the data...
      if (this.byCampaign && !R.prop('id', this.campaign)) {
        return
      }

      if (R.prop('id', this.campaign)) {
        newStatParams.campaign_id = this.campaign.id.toString()
      }

      StatsApi.fetchDailyStats(newStatParams)
        .then(stats => {
          this.dailyStats = stats
        })
        .catch(error => {
          this.$logger.error('Error loading daily stats from API', error.message)
          this.$flash({
            showClose: true,
            message: `Error loading daily stats from API: ${error.message}`,
            type: 'mini',
            duration: 3000
          })
        })
    },
    refreshChart() {
      this.loadDailyStats()
      this.findOrCreateChart(this.chartName, this.lineChartData)
    }
  },
  mounted: function () {
    this.refreshChart()
  }
}
</script>

<style></style>
